/**
 * Match Lobby Layout
 *
 * Adaptive layout for the active match flow. Renders three modes:
 * - 2 teams: Flanking columns with flow center in the middle
 * - 3+ teams: Full-width center with team grid below
 * - No teams / FFA: Full-width center with flat player grid below
 *
 * This is a pure presentation component — it receives resolved flow data
 * and renders the appropriate layout. Terminal states (loading, completed,
 * failed, cancelled) are handled by the consumer.
 */

import type { FlowContext, FlowPlayer, NodeState, QueueRankConfig } from "@rivaled/shared";
import { getTeamNames } from "@rivaled/shared";
import { useMemo } from "react";
import { useCurrentPlayerId } from "../hooks/useCurrentPlayerId";
import { FlowCenter } from "./FlowCenter";
import { getNodePlayerStatus } from "./getNodePlayerStatus";
import { PlayerGrid } from "./PlayerGrid";
import { TeamColumn } from "./TeamColumn";
import { TeamGrid } from "./TeamGrid";
import { resolveTeams } from "./types";

type MatchLobbyLayoutProps = {
  players: FlowPlayer[];
  context: FlowContext;
  currentNode: NodeState | null;
  progress: { completed: number; total: number };
  timeRemaining: number | null;
  onAction: (type: string, payload: unknown) => Promise<{ success: boolean; error?: string }>;
  isConnected?: boolean;
};

export function MatchLobbyLayout({
  players,
  context,
  currentNode,
  progress,
  timeRemaining,
  onAction,
  isConnected = true,
}: MatchLobbyLayoutProps) {
  const currentPlayerId = useCurrentPlayerId();

  // Resolve teams from flow context
  const teamNames = getTeamNames(context.matchId, context.teamCount);
  const { hasTeams, teamCount, teams, unassignedPlayers } = resolveTeams(
    players,
    context,
    currentPlayerId,
    teamNames,
  );

  // Resolve party color anchor
  const myPartyId = players.find((p) => p.userId === currentPlayerId)?.partyId ?? null;
  const rankConfig = (context.rankConfig ?? null) as QueueRankConfig | null;

  // Derive per-player status indicators from current node state
  const playerStatusSlot = useMemo(() => getNodePlayerStatus(currentNode), [currentNode]);

  // Nodes that manage their own team/player display — layout should not render external rosters
  const nodeManagesOwnRosters =
    currentNode?.nodeType === "captain_draft" ||
    currentNode?.nodeType === "self_select" ||
    currentNode?.nodeType === "auto_balance";

  // Connection warning
  const connectionWarning = !isConnected && (
    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
      <p className="text-yellow-500 text-sm">Connection lost. Attempting to reconnect...</p>
    </div>
  );

  const flowCenterProps = {
    currentNode,
    players,
    context,
    progress,
    timeRemaining,
    onAction,
  };

  // 2 teams — flanking layout (skip side columns when node manages its own rosters)
  if (hasTeams && teamCount === 2 && !nodeManagesOwnRosters) {
    return (
      <div className="@container flex flex-col gap-4">
        {connectionWarning}
        <div className="grid grid-cols-1 @lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] gap-4">
          <TeamColumn
            team={teams[0]}
            myPartyId={myPartyId}
            rankConfig={rankConfig}
            side="left"
            getPlayerStatus={playerStatusSlot}
          />
          <FlowCenter {...flowCenterProps} />
          <TeamColumn
            team={teams[1]}
            myPartyId={myPartyId}
            rankConfig={rankConfig}
            side="right"
            getPlayerStatus={playerStatusSlot}
          />
        </div>
        {unassignedPlayers.length > 0 && (
          <PlayerGrid
            players={unassignedPlayers}
            myPartyId={myPartyId}
            rankConfig={rankConfig}
            getPlayerStatus={playerStatusSlot}
          />
        )}
      </div>
    );
  }

  // 3+ teams — center + grid below (skip team grid when node manages its own rosters)
  if (hasTeams && teamCount >= 3 && !nodeManagesOwnRosters) {
    return (
      <div className="@container flex flex-col gap-3">
        {connectionWarning}
        <FlowCenter {...flowCenterProps} />
        <TeamGrid
          teams={teams}
          myPartyId={myPartyId}
          rankConfig={rankConfig}
          getPlayerStatus={playerStatusSlot}
        />
        {unassignedPlayers.length > 0 && (
          <PlayerGrid
            players={unassignedPlayers}
            myPartyId={myPartyId}
            rankConfig={rankConfig}
            getPlayerStatus={playerStatusSlot}
          />
        )}
      </div>
    );
  }

  // No teams / FFA / nodes that manage own rosters — center + optional flat player grid
  return (
    <div className="@container flex flex-col gap-3">
      {connectionWarning}
      <FlowCenter {...flowCenterProps} />
      {!nodeManagesOwnRosters && (
        <PlayerGrid
          players={players}
          myPartyId={myPartyId}
          rankConfig={rankConfig}
          getPlayerStatus={playerStatusSlot}
        />
      )}
    </div>
  );
}
